import React, { useEffect } from 'react';
import useToolStore, { TOOL_TYPES } from '../../../store/useToolStore';
import { generateCode } from '../../../utils/codeGenerators';

const GridGenerator = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.GRID]);
  const updateConfig = useToolStore((state) => state.updateToolConfig);
  const setGeneratedCode = useToolStore((state) => state.setGeneratedCode);

  useEffect(() => {
    const code = generateCode(TOOL_TYPES.GRID, config);
    setGeneratedCode(code);
  }, [config, setGeneratedCode]);

  const set = (patch) => updateConfig(TOOL_TYPES.GRID, patch);

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Grid Generator</h2>

      <div className="space-y-4">
        <label className="block">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Mode</div>
          <select
            value={config.mode}
            onChange={(e) => set({ mode: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="adaptive">Adaptive columns</option>
            <option value="fixed">Fixed columns</option>
          </select>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {config.mode === 'fixed' ? (
            <label className="block">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Column count</div>
              <input
                type="number"
                min="1"
                value={config.fixedCount}
                onChange={(e) => set({ fixedCount: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </label>
          ) : (
            <label className="block">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Min item width</div>
              <input
                type="number"
                min="40"
                value={config.adaptiveMinWidth}
                onChange={(e) => set({ adaptiveMinWidth: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </label>
          )}

          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Item count (example)</div>
            <input
              type="number"
              min="1"
              value={config.itemCount}
              onChange={(e) => set({ itemCount: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Column spacing</div>
            <input
              type="number"
              min="0"
              value={config.columnSpacing}
              onChange={(e) => set({ columnSpacing: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Row spacing</div>
            <input
              type="number"
              min="0"
              value={config.rowSpacing}
              onChange={(e) => set({ rowSpacing: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export const GridPreviewComponent = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.GRID]);
  const count = Math.max(1, Number(config.itemCount) || 12);

  const cols =
    config.mode === 'fixed'
      ? `repeat(${Math.max(1, Number(config.fixedCount) || 2)}, minmax(0, 1fr))`
      : `repeat(auto-fit, minmax(${Math.max(40, Number(config.adaptiveMinWidth) || 120)}px, 1fr))`;

  return (
    <div className="h-full w-full bg-gray-50 dark:bg-gray-900 p-4 overflow-auto">
      <div
        className="grid"
        style={{
          gridTemplateColumns: cols,
          columnGap: `${Math.max(0, Number(config.columnSpacing) || 0)}px`,
          rowGap: `${Math.max(0, Number(config.rowSpacing) || 0)}px`,
        }}
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="h-20 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-sm text-gray-700 dark:text-gray-300"
          >
            Item {i + 1}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GridGenerator;

